// The runtime allowlist governs credential access; this grammar also recognizes
// field selectors and whole-value references for display without resolving secrets.
const TOKEN_SOURCE = String.raw`\{\{\s*credential:(vlc_[0-9a-z]+)(?:#([a-zA-Z0-9_]+))?\s*\}\}`;

/** Each caller needs a fresh regex so scans cannot share lastIndex state. */
export function credentialTokenRegex(): RegExp {
  return new RegExp(TOKEN_SOURCE, "gi");
}

export function hasCredentialToken(text: string): boolean {
  return new RegExp(TOKEN_SOURCE, "i").test(text);
}

export type CredentialResolveFailure =
  | "not_granted" // id is not in the pinned version's credential list
  | "missing" // no such credential in the workspace
  | "revoked" // credential exists but is revoked
  | "field_missing" // a field was required/named but not present on the value
  | "not_scalar" // the resolved value is not a string we can type
  | "unavailable" // couldn't be retrieved (e.g. a decrypt/KMS/DB error)
  | "key_vault_refused"; // the bridge could not read it from the customer's Key Vault; `detail` says why

type CredentialFailure = {
  id: string;
  field: string | null;
  reason: CredentialResolveFailure;
  /** The bridge's own sentence, naming the fix; never a value. */
  detail?: string;
};

export type CredentialResolution =
  | { ok: true; value: string }
  | { ok: false; reason: CredentialResolveFailure; detail?: string };

/**
 * Resolve ONE token reference to a real secret value. Implemented by the
 * Vault-backed resolver in `credential-resolver.ts`; the substitution walk
 * calls it once per unique `(id, field)` in a `type` action.
 */
export type CredentialTokenResolver = (ref: {
  id: string;
  field: string | null;
}) => Promise<CredentialResolution>;

export type SubstituteResult =
  | { ok: true; text: string; usedIds: string[] }
  | {
      ok: false;
      error: string;
      failures: CredentialFailure[];
    };

/**
 * Replace every credential token in `text` with its resolved secret. ALL tokens
 * must resolve: if any fails, returns `ok: false` with a redacted, model-safe
 * error and emits NO partially-substituted string — the caller MUST refuse the
 * action rather than type a literal placeholder or a partial secret.
 */
export async function substituteCredentialTokens(
  text: string,
  resolve: CredentialTokenResolver,
): Promise<SubstituteResult> {
  const matches = [...text.matchAll(credentialTokenRegex())];
  if (matches.length === 0) return { ok: true, text, usedIds: [] };

  // Resolve each distinct (id, field) once; a single action may repeat one.
  const resolutions = new Map<string, CredentialResolution>();
  const usedIds = new Set<string>();
  const failures: CredentialFailure[] = [];

  for (const m of matches) {
    const id = m[1] as string;
    const field = (m[2] as string | undefined) ?? null;
    const key = `${id}#${field ?? ""}`;
    if (resolutions.has(key)) continue;
    const res = await resolve({ id, field });
    resolutions.set(key, res);
    if (res.ok) usedIds.add(id);
    else
      failures.push({
        id,
        field,
        reason: res.reason,
        ...(res.detail ? { detail: res.detail } : {}),
      });
  }

  if (failures.length > 0) return { ok: false, error: describeFailures(failures), failures };

  const out = text.replace(credentialTokenRegex(), (_full, id: string, field?: string) => {
    const res = resolutions.get(`${id}#${field ?? ""}`);
    // Guaranteed `ok` here — we returned early on any failure above.
    return res && res.ok ? res.value : "";
  });
  return { ok: true, text: out, usedIds: [...usedIds] };
}

const FAILURE_HINT: Record<CredentialResolveFailure, string> = {
  not_granted: "not available to this agent",
  missing: "no longer exists",
  revoked: "has been revoked",
  field_missing:
    "is missing the requested field (try a specific field like #username or #password)",
  not_scalar: "is not a value that can be typed",
  unavailable: "could not be retrieved right now",
  key_vault_refused: "could not be read from the customer's Key Vault",
};

/** Model-safe summary of why an exchange was refused — never echoes a value. */
function describeFailures(failures: CredentialFailure[]): string {
  return failures
    .map((f) => {
      const ref = f.field ? `${f.id}#${f.field}` : f.id;
      return `credential ${ref} ${FAILURE_HINT[f.reason]}${f.detail ? ` (${f.detail})` : ""}`;
    })
    .join("; ");
}
